import { action, computed, observable } from 'mobx';
import { FieldState, FormState } from '../../../../states/FormState';
import { NewCardInfo } from '../../../../types/card';
import { UserApis } from '../../../../api';
import { checkState } from '../../../../utils/preconditions';
import { RemoteResourceV2, updateRemoteResourceV2 } from '../../../../../commonv2/api/RemoteResourceV2';

type CardSummary = {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
};

export class NewCardFormState extends FormState<NewCardInfo> {
    constructor() {
        super({
            number: new FieldState('', false),
            exp: new FieldState('', false),
            cvc: new FieldState('', false),
        });
    }
}

export type SplitCount = 1 | 2 | 4 | 8 | 12;

export type SplitOption = {
    splitCount: SplitCount;
    label: string;
    // whether to show this as a primary option or secondary option with some indents
    primary: boolean;
};

export class SplitMethodFormState {
    @observable splitCount: SplitCount = 1;
    getSplitOptions(): SplitOption[] {
        return [
            { splitCount: 1, label: '一括払い', primary: false },
            { splitCount: 12, label: '分割払い（12ヶ月）', primary: true },
            { splitCount: 2, label: '2ヶ月で払う', primary: false },
            { splitCount: 4, label: '4ヶ月で払う', primary: false },
            { splitCount: 8, label: '8ヶ月で払う', primary: false },
        ];
    }
    @computed
    get currentOption(): SplitOption {
        return this.getSplitOptions().find((o) => o.splitCount === this.splitCount);
    }

    computeMonthlyPaymentWithTax(priceWithoutTax: number, priceWithTax: number, splitCount: SplitCount) {
        const totalPriceWithTax =
            priceWithTax + priceWithoutTax * SplitMethodFormState.calculateInterestRate(splitCount);
        return Math.ceil(totalPriceWithTax / splitCount);
    }

    static calculateInterestRate(splictCount: SplitCount): number {
        switch (splictCount) {
            case 1:
            case 2:
                return 0;
            case 4:
                return 0.02;
            case 8:
                return 0.04;
            case 12:
                return 0.08;
        }
    }
}

export class CardPaymentState {
    @observable useNewCard = true;
    @observable originalCard: RemoteResourceV2<CardSummary> = RemoteResourceV2.empty();
    @observable currentCard: RemoteResourceV2<CardSummary> = RemoteResourceV2.empty();
    @observable newCardForm: FormState<NewCardInfo> = null;
    @observable splitMethodForm: SplitMethodFormState = new SplitMethodFormState();
    newCardToken: string = null;

    constructor() {
        this.newCardForm = new NewCardFormState();
    }

    @action
    async loadOriginalCard(): Promise<CardSummary> {
        try {
            this.originalCard.loadState = true;
            const card = await updateRemoteResourceV2(
                this.currentCard,
                (v) => (this.currentCard = v),
                () => UserApis.getCurrentCard()
            );
            this.originalCard.value = card;
            return card;
        } finally {
            this.originalCard.loadState = false;
        }
    }

    @action
    hasCurrentCard() {
        return !this.currentCard.isEmpty();
    }

    isRequiredFieldsFilled(): boolean {
        return (
            (!this.useNewCard && !this.currentCard.isEmpty()) ||
            (this.useNewCard && this.newCardForm.isRequiredFieldsFilled())
        );
    }

    canSubmit(): boolean {
        // validateCardInfo should set the card info to currentCard if it's valid
        return !this.currentCard.isEmpty();
    }

    @action
    async validateCardInfo() {
        this.newCardToken = null;
        this.newCardForm.clearErrors();
        this.currentCard.value = null;
        try {
            const response = await UserApis.createToken(this.newCardForm.asJson());
            this.currentCard.value = response.card;
            this.newCardToken = response.id;
        } catch (error) {
            switch (error.code) {
                case 'invalid_expiry_year':
                case 'invalid_expiry_month':
                    this.newCardForm.setErrors({ exp: ['有効期限はMM/YYの形式で入力してください'] });
                    break;
                case 'incorrect_card_data':
                    this.newCardForm.setErrors({ number: ['カード情報が正しくありません'] });
                    break;
                default:
                    this.newCardForm.setErrors({ number: [error.message] });
            }
            this.newCardToken = null;
        }
    }

    @action
    async submitNewCard() {
        checkState(this.newCardToken != null, 'Card token must be set');
        await UserApis.updateCard(this.newCardToken);
        await this.loadOriginalCard();
    }
}
